import api from '@/api';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useJobStream } from '@/hooks/useJobStream';

export function useJobActions(jobId) {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [exportingPackage, setExportingPackage] = useState(false);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [packageResult, setPackageResult] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLinks, setShareLinks] = useState([]);
  const [newLink, setNewLink] = useState({ client_name: '', client_email: '', note: '', expires_in_days: 30 });
  const [creatingLink, setCreatingLink] = useState(false);
  const [createdLink, setCreatedLink] = useState(null);
  const [copiedCode, setCopiedCode] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [failedPageCount, setFailedPageCount] = useState(0);

  const fetchJob = useCallback(async () => {
    try {
      const res = await api.get(`/drawing-jobs/${jobId}`);
      setJob(res.data);
    } catch (err) {
      console.error('Failed to load job:', err);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  const fetchFailedPages = useCallback(async () => {
    try {
      const res = await api.get(`/drawing-jobs/${jobId}/progress`);
      setFailedPageCount(res.data.pages_errored || 0);
    } catch {}
  }, [jobId]);

  useEffect(() => { fetchJob(); fetchFailedPages(); }, [fetchJob, fetchFailedPages]);

  // SSE-driven status updates during processing — replaces interval polling
  const isProcessing = job?.status === 'processing';
  const { progress: streamProgress } = useJobStream(jobId, {
    enabled: isProcessing,
    onComplete: () => {
      fetchJob();
      fetchFailedPages();
    },
  });

  // Update pipeline_stage in real time from SSE stream
  useEffect(() => {
    if (streamProgress && isProcessing && streamProgress.pipeline_stage) {
      setJob(prev => prev ? { ...prev, pipeline_stage: streamProgress.pipeline_stage } : prev);
    }
  }, [streamProgress, isProcessing]);

  const approveAll = async () => {
    setApproving(true);
    try {
      await api.post(`/drawing-jobs/${jobId}/approve-all`);
      await fetchJob();
    } catch (err) {
      console.error('Approve failed:', err);
    } finally {
      setApproving(false);
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.get(`/drawing-jobs/${jobId}/export`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `building_model_${jobId}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const downloadBinaryExport = (data, filename, mimeType) => {
    const byteChars = atob(data);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
    const blob = new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportPackage = async () => {
    setExportingPackage(true);
    setShowPackageModal(true);
    setPackageResult(null);
    try {
      const res = await api.post(`/drawing-jobs/${jobId}/export-package`);
      setPackageResult(res.data);
    } catch (err) {
      console.error('Package export failed:', err);
      setPackageResult({ error: err.response?.data?.detail || 'Export failed' });
    } finally {
      setExportingPackage(false);
    }
  };

  const downloadPackage = () => {
    if (!packageResult?.data) return;
    downloadBinaryExport(packageResult.data, packageResult.filename || 'digitization_package.zip', 'application/zip');
  };

  const handleExportDxf = async () => {
    try {
      const res = await api.post(`/drawing-jobs/${jobId}/export/dxf`);
      downloadBinaryExport(res.data.data, res.data.filename || 'export.dxf', 'application/dxf');
    } catch (err) {
      console.error('DXF export failed:', err);
    }
  };

  const handleExportPdf = async () => {
    try {
      const res = await api.post(`/drawing-jobs/${jobId}/export/pdf`);
      downloadBinaryExport(res.data.data, res.data.filename || 'report.pdf', 'application/pdf');
    } catch (err) {
      console.error('PDF export failed:', err);
    }
  };

  const fetchShareLinks = async () => {
    try {
      const res = await api.get(`/share-links/job/${jobId}`);
      setShareLinks(res.data);
    } catch (err) {
      console.error('Failed to load share links:', err);
    }
  };

  const openShareModal = async () => {
    setShowShareModal(true);
    setCreatedLink(null);
    await fetchShareLinks();
  };

  const createShareLink = async () => {
    setCreatingLink(true);
    try {
      const res = await api.post('/share-links', { job_id: jobId, ...newLink });
      setCreatedLink(res.data);
      setNewLink({ client_name: '', client_email: '', note: '', expires_in_days: 30 });
      await fetchShareLinks();
    } catch (err) {
      console.error('Share link creation failed:', err);
    } finally {
      setCreatingLink(false);
    }
  };

  const revokeLink = async (linkId) => {
    try {
      await api.post(`/share-links/${linkId}/revoke`);
      await fetchShareLinks();
    } catch (err) {
      console.error('Revoke failed:', err);
    }
  };

  const getPortalUrl = (link) => `${window.location.origin}/portal/${link.share_token}`;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const handleReview = async (entityType, entityId, action) => {
    try {
      await api.post(`/drawing-jobs/${jobId}/review`, {
        decisions: [{ entity_type: entityType, entity_id: entityId, action }],
      });
      await fetchJob();
    } catch (err) {
      console.error('Review failed:', err);
    }
  };

  const handleRetryFailed = async () => {
    setRetrying(true);
    try {
      await api.post(`/drawing-jobs/${jobId}/retry-failed`);
      await fetchJob();
    } catch (err) {
      console.error('Retry failed:', err);
    } finally {
      setRetrying(false);
    }
  };

  return {
    job, loading, fetchJob,
    approving, approveAll,
    handleExport, handleExportDxf, handleExportPdf,
    exportingPackage, showPackageModal, setShowPackageModal, packageResult, handleExportPackage, downloadPackage,
    showShareModal, setShowShareModal, shareLinks, newLink, setNewLink, creatingLink, createdLink, copiedCode,
    openShareModal, createShareLink, revokeLink, getPortalUrl, copyToClipboard,
    handleReview,
    retrying, failedPageCount, handleRetryFailed,
  };
}
